import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, TrendingDown, Target, BookOpen, Lock, BarChart3, ArrowLeft } from "lucide-react";

const PrelimsWeakAreaAnalyticsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const isPro = user?.role === "PRO";

  // Mock performance data (in production, this would come from user's test history)
  const mockPerformanceData = {
    test_attempts: 5,
    overall_accuracy: 68,
    subject_wise: {
      "Polity": { attempted: 50, correct: 35, accuracy: 70 },
      "Economy": { attempted: 40, correct: 24, accuracy: 60 },
      "Geography": { attempted: 45, correct: 38, accuracy: 84 },
      "History": { attempted: 30, correct: 18, accuracy: 60 },
      "Environment": { attempted: 35, correct: 28, accuracy: 80 }
    }
  };

  const handleAnalyze = async () => {
    if (!isPro) {
      toast({
        variant: "destructive",
        description: "🔒 This feature is PRO-only. Upgrade to unlock Weak Area Analytics."
      });
      navigate("/upgrade");
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/prelims/analytics/weak-areas`,
        {
          user_performance: mockPerformanceData
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: "Weak area analysis complete!"
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to analyze weak areas"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isPro) {
      handleAnalyze();
    }
  }, [isPro]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Weak Area Analytics</h1>
          <p className="text-sm text-slate-600">
            Identify weak areas & get personalized improvement plan
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-red-500"}>
          {isPro ? "⭐ PRO" : "🔒 PRO ONLY"}
        </Badge>
      </div>

      {/* PRO-Only Lock Screen */}
      {!isPro && (
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-300">
          <CardContent className="p-12">
            <div className="text-center space-y-4">
              <Lock className="w-16 h-16 mx-auto text-red-600" />
              <h2 className="text-2xl font-bold text-slate-900">
                PRO Feature Locked
              </h2>
              <p className="text-slate-700 max-w-lg mx-auto">
                Get detailed analytics on your weak topics, subject-wise accuracy breakdown, 
                and personalized improvement plans with NCERT chapter recommendations.
              </p>
              <div className="pt-4">
                <Button className="bg-gradient-to-r from-red-500 to-rose-600 text-white px-8">
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Interface (PRO) */}
      {isPro && (
        <>
          {loading && (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-600" />
                  <p className="text-slate-600">Analyzing your performance data...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {result && (
            <div className="space-y-6">
              {/* Overall Performance */}
              <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">Overall Performance</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-slate-600">Test Attempts</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {mockPerformanceData.test_attempts}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-600">Overall Accuracy</p>
                      <p className="text-3xl font-bold text-green-600">
                        {mockPerformanceData.overall_accuracy}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-600">Weak Topics</p>
                      <p className="text-3xl font-bold text-red-600">
                        {result.weak_topics?.length || 0}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subject-wise Accuracy */}
              {result.accuracy_by_topic && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-slate-900">
                        Subject-wise Accuracy
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(result.accuracy_by_topic).map(([subject, accuracy]) => {
                        const subjectData = mockPerformanceData.subject_wise[subject];
                        const barWidth = accuracy;
                        const color = accuracy >= 75 ? "bg-green-500" : accuracy >= 60 ? "bg-yellow-500" : "bg-red-500";
                        
                        return (
                          <div key={subject}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700">{subject}</span>
                              <span className="text-sm font-bold text-slate-900">{accuracy}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-3">
                              <div
                                className={`${color} h-3 rounded-full transition-all`}
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                            {subjectData && (
                              <p className="text-xs text-slate-500 mt-1">
                                {subjectData.correct}/{subjectData.attempted} correct
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Weak Topics */}
              {result.weak_topics && result.weak_topics.length > 0 && (
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                      <h3 className="font-semibold text-slate-900">
                        Priority Weak Topics
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {result.weak_topics.map((topic, idx) => (
                        <div
                          key={idx}
                          className="bg-white border border-red-300 rounded-lg p-3"
                        >
                          <div className="flex items-center gap-2">
                            <Badge className="bg-red-600">{idx + 1}</Badge>
                            <span className="text-sm font-medium text-slate-900">{topic}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Priority Order */}
              {result.priority_order && result.priority_order.length > 0 && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-slate-900">
                        Study Priority Order
                      </h3>
                    </div>
                    <ol className="space-y-2">
                      {result.priority_order.map((subject, idx) => (
                        <li key={idx} className="flex items-center gap-3">
                          <Badge className="bg-amber-600 min-w-[30px] justify-center">
                            {idx + 1}
                          </Badge>
                          <span className="text-sm text-slate-700">{subject}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}

              {/* Improvement Plan */}
              {result.improvement_plan && result.improvement_plan.length > 0 && (
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-slate-900">
                        Personalized Improvement Plan
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {result.improvement_plan.map((step, idx) => (
                        <li key={idx} className="flex gap-3 bg-white border border-green-200 rounded-lg p-3">
                          <span className="font-semibold text-green-600 min-w-[24px]">
                            {idx + 1}.
                          </span>
                          <span className="text-sm text-slate-700">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Refresh Button */}
              <Button
                onClick={handleAnalyze}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                Refresh Analysis
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrelimsWeakAreaAnalyticsPage;
